import { useEffect, useState } from "react";

import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";

import type { GetLeadDetailsResponse } from "@pbz/schema/lead";

import AdditionalDetails from "@/components/createUpdateLead/AdditionalDetail";
import ApplicationReferenceNumber from "@/components/createUpdateLead/ApplicationReferenceNumber";
import CustomerDetails from "@/components/createUpdateLead/CustomerDetail";
import SelectPlan from "@/components/createUpdateLead/SelectPlan";
import { useGetLead } from "@/queries/lead";

interface CreateLeadProps {
  countryProductId?: number;
  editLeadId?: number;
}

function formatLeadResponse(leadData?: GetLeadDetailsResponse) {
  if (!leadData?.leadId) {
    return;
  }

  // Customer details map to LeadCreateRequest
  const customerDetails = {
    name: leadData.name ?? "",
    mobileNumber: leadData.mobileNumber,
    email: leadData.email ?? "",
    homeCountry: leadData.nationalityId ?? -1,
    city: leadData.cityId ?? -1,
    income: leadData.income ?? 0,
  };

  // Additional details map to LeadUpdateRequest
  const additionalDetails = {
    employerNameDD: leadData.employerNameDD,
    employmentTypeDD: leadData.employmentTypeDD,
    creditHistoryAgeDD: leadData.creditHistoryAgeDD,
    creditScoreRangeDD: leadData.creditScoreRangeDD,
    creditCardDesignationDD: leadData.creditCardDesignationDD,
    websiteDD: leadData.websiteDD,
    landLineDD: leadData.landLineDD,
    existingCreditDurationDD: leadData.existingCreditDurationDD,
    salaryAccountBankDD: leadData.salaryAccountBankDD,
    sellerIdDD: leadData.sellerIdDD,
    emiratesId: leadData.emiratesId,
    creditHistoryDD: leadData.creditHistoryDD,
  };

  // Plan and ARN details placeholders (extend when needed by components)
  const planDetails =
    leadData.selectionData?.insurerId && leadData.selectionData?.planId
      ? {
          bankId: leadData.selectionData.insurerId,
          planId: leadData.selectionData.planId,
        }
      : undefined;
  const arnDetails = {
    leadId: leadData.leadId,
    arnNumber: leadData.arnNumber ?? "",
  };

  return {
    customerDetails,
    additionalDetails,
    planDetails,
    arnDetails,
  };
}

const CreateLead: React.FC<CreateLeadProps> = ({
  countryProductId = 7,
  editLeadId,
}) => {
  const navigate = useNavigate();
  const isEditMode = !!editLeadId;
  const [leadId, setleadId] = useState<number | undefined>(editLeadId);
  const [arn, saveARN] = useState("");
  const [planSelected, setPlanSelected] = useState(false);
  const {
    data: leadData,
    isLoading: loadingLeadData,
    error: getLeadsError,
  } = useGetLead(
    {
      leadId: editLeadId || -1,
    },
    !!editLeadId,
  );

  useEffect(() => {
    if (getLeadsError) {
      navigate({
        to: "/lead/create",
      });

      toast.error(getLeadsError.message || "Lead Not Found");
    }
  }, [getLeadsError, navigate]);

  useEffect(() => {
    // save ARN
    leadData?.data.arnNumber && saveARN(leadData.data.arnNumber);
    //
    leadData?.data.selectionData && setPlanSelected(true);
  }, [leadData]);

  const leadDetails = formatLeadResponse(leadData?.data);

  const postLeadCreation = (pbMatrixLeadId: number) => {
    if (isEditMode) {
      return;
    }
    setleadId(pbMatrixLeadId);
  };

  if (loadingLeadData) {
    return (
      <div className="container mx-auto p-4">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading lead data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">
          {isEditMode ? "Edit Lead" : "Sell Now"}
        </h1>
        {leadId && <p className="text-gray-500">Lead ID: {leadId}</p>}
      </div>
      <CustomerDetails
        // @ts-expect-error FIXME
        defaultValues={leadDetails?.customerDetails}
        leadId={leadId}
        onSubmit={postLeadCreation}
        productId={countryProductId}
      />
      {leadId && (
        <>
          <AdditionalDetails
            defaultValues={leadDetails?.additionalDetails}
            leadId={leadId}
          />
          <SelectPlan
            arn={arn}
            defaultValues={leadDetails?.planDetails}
            leadId={leadId}
            onSuccess={() => setPlanSelected(true)}
          />
          {planSelected && (
            <ApplicationReferenceNumber
              defaultValues={leadDetails?.arnDetails}
              leadId={leadId}
              onSuccess={(arnNumber) => saveARN(arnNumber)}
            />
          )}
        </>
      )}
    </div>
  );
};

export default CreateLead;
